import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Link2Off, Loader2 } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { API, formatApiError } from "@/lib/api";
import AccessShell from "@/components/AccessShell";

// Confirmation page behind the email buttons (mail scanners must not trigger the action).
export default function AccessDecision() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const approve = params.get("aktion") !== "ablehnen";
  const [req, setReq] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/access-requests/token/${encodeURIComponent(token)}`)
      .then((r) => setReq(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [token]);

  const decide = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/access-requests/token/${encodeURIComponent(token)}/decide`, { action: approve ? "approve" : "reject" });
      setDone(r.data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (error) return (
    <AccessShell testid="access-decision-invalid">
      <Link2Off className="h-10 w-10 text-slate-400 mb-3" />
      <p className="font-semibold text-slate-900" data-testid="access-decision-error">{error}</p>
    </AccessShell>
  );
  if (done) return (
    <AccessShell testid="access-decision-done">
      {done.status === "approved" ? <CheckCircle2 className="h-10 w-10 text-green-600 mb-3" /> : <XCircle className="h-10 w-10 text-red-600 mb-3" />}
      <p className="font-semibold text-slate-900" data-testid="access-decision-result">
        {done.status === "approved"
          ? `${req.name || req.email} wurde freigegeben${done.mail_sent ? " und per E-Mail informiert" : ". Die Info-Mail konnte nicht gesendet werden – bitte den Nutzer selbst informieren"}.`
          : `Die Anfrage von ${req.name || req.email} wurde abgelehnt.`}
      </p>
    </AccessShell>
  );
  if (!req) return <AccessShell testid="access-decision-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></AccessShell>;
  return (
    <AccessShell testid="access-decision-confirm">
      <h1 className="text-2xl font-head font-extrabold tracking-tight mb-4">{approve ? "Zugang freigeben?" : "Anfrage ablehnen?"}</h1>
      <div className="flex items-center gap-3 mb-6">
        {req.picture ? <img src={req.picture} alt="" className="h-12 w-12 rounded-full object-cover" referrerPolicy="no-referrer" />
          : <div className="h-12 w-12 rounded-full bg-slate-200" />}
        <div className="min-w-0">
          <div className="font-semibold truncate" data-testid="access-decision-name">{req.name}</div>
          <div className="text-sm text-slate-500 truncate" data-testid="access-decision-email">{req.email}</div>
        </div>
      </div>
      <Button onClick={decide} disabled={busy} data-testid="access-decision-confirm-btn"
        className={`w-full h-11 ${approve ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}>
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{approve ? "Jetzt freigeben" : "Jetzt ablehnen"}
      </Button>
      {approve && (
        <p className="text-xs text-slate-500 mt-3" data-testid="access-decision-own-hint">
          Über diesen Link bekommt der Nutzer eine eigene Firma. Soll er einer bestehenden Firma beitreten, bitte im Admin-Bereich unter „Zugangsanfragen“ freigeben.
        </p>
      )}
    </AccessShell>
  );
}
